import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  BuildOutlined,
  CheckSquareOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  FlagOutlined,
  HistoryOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../api/errors';
import {
  ProjectPriority,
  ProjectTaskProgressMethod,
  ProjectTaskStatus,
  changeProjectTaskStatus,
  createProjectTask,
  deleteProjectTask,
  getProject,
  listProjectTasks,
  reportProjectTaskProgress,
  updateProjectTask,
} from '../api/projects';
import type {
  ProjectDetail,
  ProjectPriorityValue,
  ProjectTaskItem,
  ProjectTaskProgressMethodValue,
  ProjectTaskStatusValue,
} from '../api/projects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { priorityOptions, priorityTag } from './projects/projectDisplay';
import { ProjectProgressLogModal } from './projects/ProjectProgressLogModal';
import { ProjectTaskAttachmentModal } from './projects/ProjectTaskAttachmentModal';
import { ProjectTaskChecklistModal } from './projects/ProjectTaskChecklistModal';
import { ProjectTaskCommentModal } from './projects/ProjectTaskCommentModal';
import { ProjectTaskMilestoneModal } from './projects/ProjectTaskMilestoneModal';
import { ProjectTaskAssignmentModal } from './projects/ProjectTaskAssignmentModal';
import { ProjectTaskDependencyModal } from './projects/ProjectTaskDependencyModal';
import { ApplyProjectTemplateModal } from './projects/ApplyProjectTemplateModal';
import {
  taskIndent,
  taskProgressCell,
  taskProgressMethodOptions,
  taskStatusOptions,
  taskStatusTag,
} from './projects/taskDisplay';

/** Giá trị của biểu mẫu tạo và sửa công việc. */
interface TaskFormValues {
  readonly name: string;
  readonly code: string | null;
  readonly description: string | null;
  readonly parentId: string | null;
  readonly priority: ProjectPriorityValue;
  readonly isMilestone: boolean;
}

/** Giá trị của biểu mẫu báo tiến độ. */
interface ProgressFormValues {
  readonly progressMethod: ProjectTaskProgressMethodValue;
  readonly progressPercent: number;
  readonly remainingEffortHours: number | null;
}

/**
 * Trang cây công việc của một dự án.
 *
 * <b>Bảng phẳng có thụt lề, không phải cây bấm mở từng nhánh.</b> Với vài trăm dòng thì bảng phẳng
 * đọc nhanh hơn hẳn: người dùng nhìn được cả bức tranh mà không phải bấm mở từng nhóm. Máy chủ đã sắp
 * theo đường dẫn trong cây nên mọi việc con nằm ngay dưới việc cha của nó.
 *
 * <b>Ô báo tiến độ đổi theo cách đo.</b> Việc đo theo cách <i>xong hoặc chưa xong</i> — mặc định của
 * mọi việc mới — chỉ có một ô đánh dấu, không có chỗ gõ phần trăm. Đó chính là cách chặn bệnh
 * <i>"90% mãi không xong"</i> ở tầng giao diện: không có ô để gõ thì không ai gõ được một con số cảm
 * tính. Muốn gõ phần trăm thì phải đổi cách đo, và hệ thống đánh dấu riêng những việc đó.
 */
export function ProjectTaskPage(): ReactNode {
  const { projectId } = useParams<{ projectId: string }>();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canManage = hasPermission(PermissionCodes.ProjectTaskManage);
  const canApplyTemplate = hasPermission(PermissionCodes.ProjectTemplateApply);
  const canUpdateProgress = hasPermission(PermissionCodes.ProjectTaskUpdateProgress);

  const [taskForm] = Form.useForm<TaskFormValues>();
  const [progressForm] = Form.useForm<ProgressFormValues>();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [tasks, setTasks] = useState<readonly ProjectTaskItem[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [editing, setEditing] = useState<ProjectTaskItem | null>(null);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [progressTarget, setProgressTarget] = useState<ProjectTaskItem | null>(null);
  const [progressMethod, setProgressMethod] = useState<ProjectTaskProgressMethodValue>(
    ProjectTaskProgressMethod.ZeroOrHundred,
  );

  const [blockTarget, setBlockTarget] = useState<ProjectTaskItem | null>(null);
  const [blockedReason, setBlockedReason] = useState('');

  const [isDependencyOpen, setIsDependencyOpen] = useState(false);
  const [isApplyTemplateOpen, setIsApplyTemplateOpen] = useState(false);

  const [checklistTarget, setChecklistTarget] = useState<ProjectTaskItem | null>(null);
  const [milestoneTarget, setMilestoneTarget] = useState<ProjectTaskItem | null>(null);
  const [commentTarget, setCommentTarget] = useState<ProjectTaskItem | null>(null);
  const [attachmentTarget, setAttachmentTarget] = useState<ProjectTaskItem | null>(null);

  /*
    Một trạng thái cho cả hai chế độ của hộp thoại nhật ký: mở kèm một công việc thì xem lịch sử của
    việc đó, mở không kèm gì thì xem những con số đáng ngờ của cả dự án.
  */
  const [isProgressLogOpen, setIsProgressLogOpen] = useState(false);
  const [progressLogTarget, setProgressLogTarget] = useState<ProjectTaskItem | null>(null);
  const [assignmentTarget, setAssignmentTarget] = useState<ProjectTaskItem | null>(null);

  useEffect(() => {
    if (projectId === undefined) {
      return;
    }

    let isCurrent = true;

    async function load(identifier: string): Promise<void> {
      setErrorMessage(null);

      try {
        const [loadedProject, loadedTasks] = await Promise.all([
          getProject(identifier),
          listProjectTasks(identifier),
        ]);

        if (!isCurrent) {
          return;
        }

        setProject(loadedProject);
        setTasks(loadedTasks);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được cây công việc.',
        );
      }
    }

    void load(projectId);

    return () => {
      isCurrent = false;
    };
  }, [projectId, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  function openCreate(parentId: string | null): void {
    setEditing(null);
    taskForm.resetFields();
    taskForm.setFieldsValue({
      parentId,
      priority: ProjectPriority.Normal,
      isMilestone: false,
    });
    setIsTaskFormOpen(true);
  }

  function openEdit(task: ProjectTaskItem): void {
    setEditing(task);
    taskForm.setFieldsValue({
      name: task.name,
      code: task.code,
      description: task.description,
      parentId: task.parentId,
      priority: task.priority,
      isMilestone: task.isMilestone,
    });
    setIsTaskFormOpen(true);
  }

  async function submitTask(values: TaskFormValues): Promise<void> {
    if (projectId === undefined) {
      return;
    }

    setIsSaving(true);

    try {
      if (editing === null) {
        await createProjectTask(projectId, {
          name: values.name,
          parentId: values.parentId ?? null,
          code: values.code ?? null,
          description: values.description ?? null,
          sortOrder: nextSortOrder(tasks, values.parentId ?? null),
          isMilestone: values.isMilestone,
        });

        message.success('Đã thêm công việc.');
      } else {
        await updateProjectTask(projectId, editing.id, {
          code: values.code ?? null,
          name: values.name,
          description: values.description ?? null,
          priority: values.priority,
          isMilestone: values.isMilestone,
        });

        message.success('Đã lưu công việc.');
      }

      setIsTaskFormOpen(false);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được công việc.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitProgress(values: ProgressFormValues): Promise<void> {
    if (projectId === undefined || progressTarget === null) {
      return;
    }

    setIsSaving(true);

    try {
      await reportProjectTaskProgress(projectId, progressTarget.id, {
        progressPercent: values.progressPercent,
        remainingEffortHours: values.remainingEffortHours ?? null,
        progressMethod: values.progressMethod,
      });

      message.success('Đã báo tiến độ.');
      setProgressTarget(null);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không báo được tiến độ.');
    } finally {
      setIsSaving(false);
    }
  }

  async function applyStatus(
    task: ProjectTaskItem,
    status: ProjectTaskStatusValue,
    reason: string | null,
  ): Promise<void> {
    if (projectId === undefined) {
      return;
    }

    try {
      await changeProjectTaskStatus(projectId, task.id, status, reason);

      setBlockTarget(null);
      setBlockedReason('');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được trạng thái.');
    }
  }

  async function removeTask(task: ProjectTaskItem): Promise<void> {
    if (projectId === undefined) {
      return;
    }

    try {
      await deleteProjectTask(projectId, task.id);

      message.success('Đã xóa công việc.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được công việc.');
    }
  }

  if (errorMessage !== null) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            void navigate(AppRoutes.Project);
          }}
        >
          Về danh sách dự án
        </Button>
        <Alert type="error" showIcon message={errorMessage} />
      </Space>
    );
  }

  if (project === null || tasks === null || projectId === undefined) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const columns: TableColumnsType<ProjectTaskItem> = [
    {
      title: 'Mã',
      dataIndex: 'wbsCode',
      width: 90,
      render: (wbsCode: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {wbsCode}
        </Typography.Text>
      ),
    },
    {
      title: 'Công việc',
      dataIndex: 'name',
      render: (name: string, task) => (
        <div style={{ paddingLeft: taskIndent(task.depth) }}>
          <Space size={4} wrap>
            <Typography.Text strong={task.isSummaryTask}>{name}</Typography.Text>
            {task.isMilestone ? <Tag color="purple">Mốc</Tag> : null}
            {task.code !== null ? <Tag>{task.code}</Tag> : null}
          </Space>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 130,
      render: (status: ProjectTaskStatusValue, task) =>
        taskStatusTag(status, task.blockedReason),
    },
    {
      title: 'Ưu tiên',
      dataIndex: 'priority',
      width: 110,
      render: (priority: ProjectPriorityValue) => priorityTag(priority),
    },
    {
      title: 'Tiến độ',
      key: 'progress',
      width: 170,
      render: (_, task) => taskProgressCell(task),
    },
    {
      title: 'Giờ còn lại',
      dataIndex: 'remainingEffortHours',
      width: 110,
      align: 'right',
      render: (hours: number | null) => hours ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 290,
      render: (_, task) => (
        <Space size={2}>
          {task.isSummaryTask ? null : (
            <Tooltip title="Báo tiến độ">
              <Button
                size="small"
                disabled={!canUpdateProgress}
                onClick={() => {
                  setProgressTarget(task);
                  setProgressMethod(task.progressMethod);
                  progressForm.setFieldsValue({
                    progressMethod: task.progressMethod,
                    progressPercent: task.progressPercent,
                    remainingEffortHours: task.remainingEffortHours,
                  });
                }}
              >
                Tiến độ
              </Button>
            </Tooltip>
          )}
          <Select<ProjectTaskStatusValue>
            size="small"
            value={task.status}
            style={{ width: 120 }}
            disabled={!canUpdateProgress}
            options={taskStatusOptions}
            onChange={(status) => {
              if (status === ProjectTaskStatus.Blocked) {
                setBlockTarget(task);
                setBlockedReason('');

                return;
              }

              void applyStatus(task, status, null);
            }}
          />
          <Tooltip
            title={
              task.isSummaryTask
                ? 'Nhóm việc không giao người được — hãy giao vào từng việc con.'
                : 'Giao việc cho người'
            }
          >
            <Button
              size="small"
              icon={<TeamOutlined />}
              disabled={task.isSummaryTask}
              onClick={() => {
                setAssignmentTarget(task);
              }}
            />
          </Tooltip>
          <Tooltip title="Tệp đính kèm — ảnh hiện trường, bản vẽ, tài liệu">
            <Button
              size="small"
              icon={<PaperClipOutlined />}
              onClick={() => {
                setAttachmentTarget(task);
              }}
            />
          </Tooltip>
          <Tooltip title="Trao đổi về công việc này">
            <Button
              size="small"
              icon={<CommentOutlined />}
              onClick={() => {
                setCommentTarget(task);
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              task.isSummaryTask
                ? 'Nhóm việc không có danh sách kiểm — tiến độ của nó tính từ việc con lên.'
                : 'Danh sách kiểm — cách đo tiến độ khách quan'
            }
          >
            <Button
              size="small"
              icon={<CheckSquareOutlined />}
              disabled={task.isSummaryTask}
              onClick={() => {
                setChecklistTarget(task);
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              task.isSummaryTask
                ? 'Nhóm việc không có mốc con — tiến độ của nó tính từ việc con lên.'
                : 'Mốc con — dành cho việc dài không chia nhỏ được'
            }
          >
            <Button
              size="small"
              icon={<FlagOutlined />}
              disabled={task.isSummaryTask}
              onClick={() => {
                setMilestoneTarget(task);
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              task.isSummaryTask
                ? 'Tiến độ của nhóm việc được tính từ việc con lên nên không có lần báo nào.'
                : 'Lịch sử báo tiến độ'
            }
          >
            <Button
              size="small"
              icon={<HistoryOutlined />}
              disabled={task.isSummaryTask}
              onClick={() => {
                setProgressLogTarget(task);
                setIsProgressLogOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Thêm việc con">
            <Button
              size="small"
              icon={<PlusOutlined />}
              disabled={!canManage}
              onClick={() => {
                openCreate(task.id);
              }}
            />
          </Tooltip>
          <Tooltip title="Sửa">
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!canManage}
              onClick={() => {
                openEdit(task);
              }}
            />
          </Tooltip>
          <Tooltip
            title={task.isSummaryTask ? 'Còn việc con nên chưa xóa được.' : 'Xóa công việc'}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={!canManage || task.isSummaryTask}
              onClick={() => {
                void removeTask(task);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Space direction="vertical" size={0}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Công việc — {project.code}
          </Typography.Title>
          <Typography.Text type="secondary">{project.name}</Typography.Text>
        </Space>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              void navigate(`${AppRoutes.Project}/${projectId}`);
            }}
          >
            Chi tiết dự án
          </Button>
          <Button
            icon={<ApartmentOutlined />}
            onClick={() => {
              setIsDependencyOpen(true);
            }}
          >
            Quan hệ phụ thuộc
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => {
              setProgressLogTarget(null);
              setIsProgressLogOpen(true);
            }}
          >
            Con số đáng ngờ
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
          {/*
            Nút áp mẫu chỉ hiện khi cây còn RỖNG. Áp lần hai nhân đôi cả cây và không có cách nào gỡ
            ra cho đúng, nên máy chủ chặn — và giấu nút đi từ trước thì người dùng không phải bấm mới
            biết là không được.
          */}
          {tasks.length === 0 && canApplyTemplate ? (
            <Button
              icon={<BuildOutlined />}
              onClick={() => {
                setIsApplyTemplateOpen(true);
              }}
            >
              Áp mẫu dự án
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => {
              openCreate(null);
            }}
          >
            Thêm công việc
          </Button>
        </Space>
      </Space>

      {tasks.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Dự án chưa có công việc nào"
          description="Áp một mẫu dự án để có sẵn cây công việc chuẩn, hoặc tự thêm việc. Hãy chia dự án thành những việc ngắn — bốn mươi việc mỗi việc ba ngày cho bức tranh trung thực hơn hẳn tám việc mỗi việc mười lăm ngày, vì lúc đó chỉ cần đếm việc đã xong."
        />
      ) : null}

      <Table<ProjectTaskItem>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={tasks}
      />

      <Modal
        open={isTaskFormOpen}
        title={editing === null ? 'Thêm công việc' : `Sửa ${editing.wbsCode} — ${editing.name}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setIsTaskFormOpen(false);
        }}
        onOk={() => {
          taskForm.submit();
        }}
      >
        <Form
          form={taskForm}
          layout="vertical"
          onFinish={(values) => void submitTask(values)}
        >
          <Form.Item
            name="name"
            label="Tên công việc"
            rules={[{ required: true, message: 'Chưa nhập tên công việc.' }]}
          >
            <Input placeholder="Chụp ảnh dataset mặt trên" />
          </Form.Item>
          <Form.Item
            name="parentId"
            label="Thuộc nhóm việc"
            extra={
              editing === null
                ? 'Để trống nghĩa là việc ở cấp cao nhất của dự án.'
                : 'Không đổi được ở đây — dùng nút di chuyển trên bảng.'
            }
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={editing !== null}
              placeholder="Cấp cao nhất"
              options={tasks.map((task) => ({
                value: task.id,
                label: `${task.wbsCode} — ${task.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="code" label="Mã do bạn đặt">
            <Input placeholder="T-001" />
          </Form.Item>
          <Form.Item name="priority" label="Mức độ ưu tiên" initialValue={ProjectPriority.Normal}>
            <Select<ProjectPriorityValue> options={priorityOptions} disabled={editing === null} />
          </Form.Item>
          <Form.Item
            name="isMilestone"
            label="Việc mốc"
            valuePropName="checked"
            initialValue={false}
            extra="Việc mốc đánh dấu một thời điểm — ký hợp đồng, bàn giao, nghiệm thu — nên nó bắt đầu và kết thúc trong cùng một ngày."
          >
            <Checkbox>Đây là việc mốc</Checkbox>
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={progressTarget !== null}
        title={progressTarget === null ? 'Báo tiến độ' : `Tiến độ — ${progressTarget.name}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setProgressTarget(null);
        }}
        onOk={() => {
          progressForm.submit();
        }}
      >
        <Form
          form={progressForm}
          layout="vertical"
          onFinish={(values) => void submitProgress(values)}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Cách đo quyết định con số nào nhận được"
            description="Với cách xong-hoặc-chưa-xong thì chỉ có hai giá trị. Đó là cách chặn bệnh 90% mãi không xong: phần lớn công việc không có chỗ để báo một con số cảm tính. Nếu việc quá dài để trả lời xong hay chưa, cách đúng là chia nhỏ nó ra."
          />
          <Form.Item name="progressMethod" label="Cách đo tiến độ">
            <Select<ProjectTaskProgressMethodValue>
              options={taskProgressMethodOptions}
              onChange={(value) => {
                setProgressMethod(value);

                if (value === ProjectTaskProgressMethod.ZeroOrHundred) {
                  progressForm.setFieldsValue({ progressPercent: 0 });
                }
              }}
            />
          </Form.Item>

          {progressMethod === ProjectTaskProgressMethod.ZeroOrHundred ? (
            <Form.Item label="Đã xong chưa">
              <Checkbox
                checked={progressForm.getFieldValue('progressPercent') === 100}
                onChange={(event) => {
                  progressForm.setFieldsValue({
                    progressPercent: event.target.checked ? 100 : 0,
                  });
                }}
              >
                Việc này đã xong
              </Checkbox>
            </Form.Item>
          ) : (
            <Form.Item
              name="progressPercent"
              label="Phần trăm hoàn thành"
              extra="Con số này sẽ được đánh dấu trên báo cáo là tiến độ tự báo."
            >
              <InputNumber<number> min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Form.Item
            name="remainingEffortHours"
            label="Giờ công còn lại"
            extra="Câu hỏi còn bao nhiêu giờ nữa xong cho câu trả lời trung thực hơn xong bao nhiêu phần trăm, vì nó buộc bạn nghĩ về phần việc còn lại cụ thể."
          >
            <InputNumber<number> min={0} max={2000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="progressPercent" hidden>
            <InputNumber<number> />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={blockTarget !== null}
        title={blockTarget === null ? 'Đánh dấu bị chặn' : `Bị chặn — ${blockTarget.name}`}
        okText="Đánh dấu bị chặn"
        cancelText="Đóng"
        okButtonProps={{ disabled: blockedReason.trim() === '' }}
        onCancel={() => {
          setBlockTarget(null);
        }}
        onOk={() => {
          if (blockTarget !== null) {
            void applyStatus(blockTarget, ProjectTaskStatus.Blocked, blockedReason);
          }
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Bắt buộc ghi lý do"
            description="Nguyên nhân thường nằm ngoài đội kỹ thuật — khách chưa gửi mẫu, thiết bị chưa về — và đó chính là thứ cần đưa vào cuộc họp với khách."
          />
          <Input.TextArea
            rows={3}
            value={blockedReason}
            placeholder="Khách chưa gửi mẫu để chụp dataset."
            onChange={(event) => {
              setBlockedReason(event.target.value);
            }}
          />
        </Space>
      </Modal>

      <ProjectTaskAssignmentModal
        projectId={projectId}
        task={assignmentTarget}
        canManage={canManage}
        open={assignmentTarget !== null}
        onClose={() => {
          setAssignmentTarget(null);
          reload();
        }}
      />

      <ApplyProjectTemplateModal
        open={isApplyTemplateOpen}
        projectId={projectId}
        projectTypeId={project.projectTypeId}
        onClose={() => {
          setIsApplyTemplateOpen(false);
        }}
        onApplied={reload}
      />

      <ProjectTaskDependencyModal
        projectId={projectId}
        tasks={tasks}
        canManage={canManage}
        open={isDependencyOpen}
        onClose={() => {
          setIsDependencyOpen(false);
        }}
      />

      <ProjectTaskChecklistModal
        projectId={projectId}
        task={checklistTarget}
        canManage={canManage}
        canReportProgress={canUpdateProgress}
        open={checklistTarget !== null}
        onClose={(hasChanged) => {
          setChecklistTarget(null);

          /*
            Chỉ tải lại cây khi có gì đó thật sự đổi. Tick một mục làm tiến độ của cả nhánh phía trên
            đổi theo, nên cây phải đọc lại; còn mở ra xem rồi đóng thì không cần một vòng gọi máy chủ.
          */
          if (hasChanged) {
            reload();
          }
        }}
      />

      {/*
        Bình luận mở được cả trên nhóm việc, khác danh sách kiểm và mốc con — bàn về cả một cụm công
        việc là chuyện bình thường. Đóng lại không cần tải lại cây vì bình luận không đụng tới tiến độ.
      */}
      {/* Tệp đính kèm không đụng tới tiến độ nên đóng lại không cần tải lại cây công việc. */}
      <ProjectTaskAttachmentModal
        projectId={projectId}
        task={attachmentTarget}
        open={attachmentTarget !== null}
        onClose={() => {
          setAttachmentTarget(null);
        }}
      />

      <ProjectTaskCommentModal
        projectId={projectId}
        task={commentTarget}
        open={commentTarget !== null}
        onClose={() => {
          setCommentTarget(null);
        }}
      />

      <ProjectTaskMilestoneModal
        projectId={projectId}
        task={milestoneTarget}
        canManage={canManage}
        canReportProgress={canUpdateProgress}
        open={milestoneTarget !== null}
        onClose={(hasChanged) => {
          setMilestoneTarget(null);

          if (hasChanged) {
            reload();
          }
        }}
      />

      <ProjectProgressLogModal
        projectId={projectId}
        task={progressLogTarget}
        open={isProgressLogOpen}
        onClose={() => {
          setIsProgressLogOpen(false);
        }}
      />
    </Space>
  );
}

/**
 * Tính thứ tự hiển thị cho một công việc mới.
 *
 * Đặt sau việc cuối cùng cùng cha. Dùng số lớn hơn hẳn thay vì đếm số việc con, vì thứ tự hiển thị
 * có thể đã bị sửa tay và hai việc trùng số sẽ để phép sắp phụ theo tên quyết định — không sai, nhưng
 * không phải thứ người dùng vừa yêu cầu.
 */
function nextSortOrder(
  tasks: readonly ProjectTaskItem[] | null,
  parentId: string | null,
): number {
  if (tasks === null) {
    return 0;
  }

  const siblings = tasks.filter((task) => task.parentId === parentId);

  return siblings.length === 0
    ? 0
    : Math.max(...siblings.map((task) => task.sortOrder)) + 1;
}
